<template>
  <basic-dialog :show="show" @close="$emit('close')" maxWidth="900">
    <template v-slot:title>
      Search result details
      <span class="subtitle-2 grey--text" v-if="title"> — {{ title }}</span>
    </template>

    <v-container class="px-0" v-if="$apollo.queries.searchResult.loading">
      <v-row justify="center" class="py-6">
        <v-progress-circular indeterminate color="primary" :size="32" />
      </v-row>
    </v-container>

    <v-alert type="warning" text v-else-if="errorMsg != ''">{{
      errorMsg
    }}</v-alert>

    <v-alert type="info" text v-else-if="!searchResult">
      The search result for this service is no longer available.
    </v-alert>

    <v-container class="px-0" v-else>
      <v-alert dense text type="info" class="mb-3">
        As quoted by {{ searchResult.providerCode || "the provider" }} on
        {{ searchResult.searchedAt | formatDate }} — not re-checked since.
      </v-alert>

      <v-simple-table dense>
        <tbody>
          <tr v-for="row in rows" :key="row.label">
            <td class="grey--text text--darken-1" style="width: 220px">
              {{ row.label }}
            </td>
            <td>{{ row.value }}</td>
          </tr>
        </tbody>
      </v-simple-table>

      <v-container class="px-0 pb-0" v-if="searchResult.cancellationPolicy">
        <div class="subtitle-2 pt-3 pb-1">Terms and conditions</div>
        <v-alert type="info" text class="mb-0">
          <div style="white-space: pre-line">
            {{ searchResult.cancellationPolicy }}
          </div>
        </v-alert>
      </v-container>

      <v-container class="px-0 pb-0" v-if="notes != ''">
        <div class="subtitle-2 pt-3 pb-1">Notes</div>
        <div style="white-space: pre-line">{{ notes }}</div>
      </v-container>

      <v-container class="px-0 pb-0" v-if="extras.length > 0">
        <div class="subtitle-2 pt-3 pb-1">Extras offered</div>
        <v-simple-table dense>
          <tbody>
            <tr v-for="extra in extras" :key="extra.id">
              <td>{{ extra.name }}</td>
              <td class="text-right">
                {{ extra.sell | priceLabeler }}
                {{ extra.currencySell || searchResult.currencyCode }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-container>

      <v-container class="px-0 pb-0" v-if="searchResult.description">
        <div class="subtitle-2 pt-3 pb-1">Description</div>
        <div v-html="searchResult.description"></div>
      </v-container>
    </v-container>
  </basic-dialog>
</template>

<script>
import gql from "graphql-tag";
import BasicDialog from "../../../shared/BasicDialog.vue";

// What the provider quoted for this service, kept on the rate search result row. The
// itinerary keeps the id of that row on the item source, so the details can be shown
// back long after the search results themselves have gone (#11452).
export const RS_RESULT_SEARCH_DETAILS = gql`
  query rsResultSearchDetails($id: ID!) {
    searchResult: rsResultService(id: $id) {
      id
      serviceName
      description
      providerCode
      supplierName
      supplierCode
      price
      currencyCode
      rateTypeCode
      basis
      roomName
      roomType
      roomBeds
      roomCode
      pickupPoint
      dropoffPoint
      pickupTime
      dropoffTime
      cancellationPolicy
      additionalNotes
      voucherNotes
      searchStartDate
      searchEndDate
      searchAdults
      searchChildren
      searchInfants
      searchedAt
      extras {
        id
        name
        sell
        currencySell
      }
    }
  }
`;

export default {
  name: "item-search-result-details",

  components: { BasicDialog },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    // Encoded RsResultService id, taken from the item's source
    rsResultServiceId: {
      type: String,
      default: "",
    },
    // Service name to show in the dialog title
    title: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      searchResult: null,
      errorMsg: "",
    };
  },

  computed: {
    // Only the lines the provider actually returned — which keys are filled depends on
    // whether the service is accommodation, a tour or a transfer.
    rows() {
      const r = this.searchResult;
      if (!r) return [];
      const room = [r.roomName, r.roomType, r.roomBeds]
        .filter((v) => v)
        .join(", ");
      const pickup = [r.pickupPoint, r.pickupTime].filter((v) => v).join(" ");
      const dropoff = [r.dropoffPoint, r.dropoffTime].filter((v) => v).join(" ");
      return [
        { label: "Supplier", value: r.supplierName },
        { label: "Service", value: r.serviceName },
        { label: "Price", value: this.priceLabel },
        { label: "Rate plan", value: r.rateTypeCode },
        { label: "Board basis", value: r.basis },
        { label: "Room", value: room },
        { label: "Pick-up", value: pickup },
        { label: "Drop-off", value: dropoff },
        { label: "Dates searched", value: this.datesLabel },
        { label: "Passengers searched", value: this.paxLabel },
      ].filter((row) => row.value && row.value.toString().trim() != "");
    },
    priceLabel() {
      const r = this.searchResult;
      if (!r || !r.price) return "";
      return `${this.$options.filters.priceLabeler(r.price)} ${
        r.currencyCode || ""
      }`.trim();
    },
    datesLabel() {
      const r = this.searchResult;
      if (!r || !r.searchStartDate) return "";
      const from = this.$options.filters.formatDate(r.searchStartDate);
      const to = this.$options.filters.formatDate(r.searchEndDate);
      return to && to != from ? `${from} - ${to}` : from;
    },
    paxLabel() {
      const r = this.searchResult;
      if (!r) return "";
      const parts = [];
      if (r.searchAdults) parts.push(`${r.searchAdults} adult(s)`);
      if (r.searchChildren) parts.push(`${r.searchChildren} child(ren)`);
      if (r.searchInfants) parts.push(`${r.searchInfants} infant(s)`);
      return parts.join(", ");
    },
    notes() {
      const r = this.searchResult;
      if (!r) return "";
      return [r.additionalNotes, r.voucherNotes].filter((v) => v).join("\n");
    },
    extras() {
      return this.searchResult && this.searchResult.extras
        ? this.searchResult.extras
        : [];
    },
  },

  apollo: {
    searchResult: {
      query: RS_RESULT_SEARCH_DETAILS,
      variables() {
        return { id: this.rsResultServiceId };
      },
      skip() {
        return (
          !this.show ||
          !this.rsResultServiceId ||
          this.rsResultServiceId.toString() == ""
        );
      },
      result() {
        this.errorMsg = "";
      },
      error(err) {
        this.errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      },
      fetchPolicy: "network-only",
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/itineraries/builder/tools/ItemSearchResultDetails.vue